import rolePermission from '@/constants/rolePermission';
import { DEFAULT_LAYOUT } from '../base';
import { AppRouteRecordRaw } from '../types';

const Production: AppRouteRecordRaw = {
  path: '/production',
  name: 'Production',
  component: DEFAULT_LAYOUT,
  meta: {
    title: '生产跟进',
    requiresAuth: true,
    icon: 'icon-brush',
    order: 4,
  },
  children: [
    {
      path: 'production-order',
      name: 'ProductionOrder',
      component: () => import('@/views/production/productionOrder/index.vue'),
      meta: {
        title: '生产单',
        requiresAuth: true,
        roles: ['produceOrderGet'],
      },
    },
    {
      path: 'process-audit',
      name: 'ProcessAudit',
      component: () => import('@/views/production/processAudit/index.vue'),
      meta: {
        title: '工序审核',
        requiresAuth: true,
        sysConfigKey: 'isOpenWorkProcessEditAudit', // 系统配置的configKey，没有默认展示
        defaultValue: 0, // sysConfigKey的默认值，新用户取不到就会取默认值，没有设置默认展示
        roles: ['goodsDataWorkProcessGet', 'produceOrderGet'],
        permissionMode: 'some',
      },
    },
    {
      path: 'production-order-bom',
      name: 'ProductionOrderBom',
      component: async () => {
        const cpn = await import('@/views/production/productionOrder/bom.vue');
        return { ...cpn.default, name: 'ProductionOrderBom' };
      },
      meta: {
        title: '生产制单',
        requiresAuth: true,
        roles: ['produceOrderGet'],
        hideInMenu: true,
      },
    },
    {
      path: 'production-order-add',
      name: 'AddProductionOrder',
      component: async () => {
        const cpn = await import(
          '@/views/production/productionOrder/addOrEdit.vue'
        );
        return { ...cpn.default, name: 'AddProductionOrder' };
      },
      props: (route: any) => ({
        copy: route.query.copy,
        ordersSn: route.query.ordersSn,
      }),
      meta: {
        title: '新增生产单',
        requiresAuth: true,
        roles: rolePermission.produceOrderEdit,
        permissionMode: 'some',
        hideInMenu: true,
      },
    },
    {
      path: 'production-draft-order-edit',
      name: 'EditProductionDraftOrder',
      component: async () => {
        const cpn = await import(
          '@/views/production/productionOrder/addOrEdit.vue'
        );
        return { ...cpn.default, name: 'EditProductionDraftOrder' };
      },
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
      }),
      meta: {
        title: '编辑生产单',
        requiresAuth: true,
        roles: ['produceOrderGet'],
        hideInMenu: true,
      },
    },
    {
      path: 'production-order-details',
      name: 'ProductionOrderDetails',
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
      }),
      component: () => import('@/views/production/productionOrder/details.vue'),
      meta: {
        title: '生产单详情',
        requiresAuth: true,
        roles: ['produceOrderGet'],
        hideInMenu: true,
        ignoreCache: true,
      },
    },
    {
      path: 'inbound-order',
      name: 'InboundOrder',
      component: () => import('@/views/production/inboundOrder/index.vue'),
      meta: {
        title: '生产入库',
        requiresAuth: true,
        roles: ['produceStoreInOrderGet'],
      },
    },
    {
      path: 'inbound-order-add',
      name: 'AddInboundOrder',
      component: async () => {
        const cpn = await import(
          '@/views/production/inboundOrder/addOrEdit.vue'
        );
        return { ...cpn.default, name: 'AddInboundOrder' };
      },
      meta: {
        title: '新增生产入库单',
        requiresAuth: true,
        roles: rolePermission.produceStoreInOrderEdit,
        permissionMode: 'some',
        hideInMenu: true,
      },
    },
    {
      path: 'inbound-order-edit',
      name: 'EditInboundOrder',
      component: async () => {
        const cpn = await import(
          '@/views/production/inboundOrder/addOrEdit.vue'
        );
        return { ...cpn.default, name: 'EditInboundOrder' };
      },
      meta: {
        title: '编辑生产入库单',
        requiresAuth: true,
        roles: ['produceStoreInOrderGet'],
        hideInMenu: true,
      },
    },
    {
      path: 'inbound-order-detail',
      name: 'inboundOrderDetail',
      component: () => import('@/views/production/inboundOrder/details.vue'),
      meta: {
        title: '生产入库单详情',
        requiresAuth: true,
        roles: ['produceStoreInOrderGet'],
        hideInMenu: true,
      },
    },
    {
      path: 'goods-rework',
      name: 'GoodsRework',
      component: () => import('@/views/production/goodsRework/index.vue'),
      meta: {
        title: '次品返厂',
        requiresAuth: true,
        roles: ['reworkOrderGet'],
      },
    },
    {
      path: 'goods-rework-add',
      name: 'AddGoodsRework',
      component: async () => {
        const cpn = await import(
          '@/views/production/goodsRework/addOrEdit.vue'
        );
        return { ...cpn.default, name: 'AddGoodsRework' };
      },
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
        copy: route.query.copy,
      }),
      meta: {
        title: '新增次品返厂单',
        requiresAuth: true,
        roles: rolePermission.reworkOrderEdit,
        permissionMode: 'some',
        hideInMenu: true,
      },
    },
    {
      path: 'goods-rework-edit',
      name: 'EditGoodsRework',
      component: async () => {
        const cpn = await import(
          '@/views/production/goodsRework/addOrEdit.vue'
        );
        return { ...cpn.default, name: 'EditGoodsRework' };
      },
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
      }),
      meta: {
        title: '编辑次品返厂单',
        requiresAuth: true,
        roles: ['reworkOrderGet'],
        hideInMenu: true,
      },
    },
    {
      path: 'goods-rework-edit',
      name: 'GoodsReworkDetail',
      component: async () => {
        const cpn = await import(
          '@/views/production/goodsRework/addOrEdit.vue'
        );
        return { ...cpn.default, name: 'GoodsReworkDetail' };
      },
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
      }),
      meta: {
        title: '次品返厂单详情',
        requiresAuth: true,
        roles: ['reworkOrderGet'],
        hideInMenu: true,
        ignoreCache: true,
      },
    },
    {
      path: 'outbound-order',
      name: 'OutboundOrder',
      component: () => import('@/views/production/outboundOrder/index.vue'),
      meta: {
        title: '生产出库',
        requiresAuth: true,
        roles: ['produceStoreOutOrderGet'],
      },
    },
    {
      path: 'outbound-order-add',
      name: 'AddOutboundOrder',
      component: async () => {
        const cpn = await import(
          '@/views/production/outboundOrder/addOrEdit.vue'
        );
        return { ...cpn.default, name: 'AddOutboundOrder' };
      },
      meta: {
        title: '新增生产出库单',
        requiresAuth: true,
        roles: rolePermission.produceStoreOutOrderEdit,
        permissionMode: 'some',
        hideInMenu: true,
      },
    },
    {
      path: 'outbound-order-edit',
      name: 'EditOutboundOrder',
      component: async () => {
        const cpn = await import(
          '@/views/production/outboundOrder/addOrEdit.vue'
        );
        return { ...cpn.default, name: 'EditOutboundOrder' };
      },
      meta: {
        title: '编辑生产出库单',
        requiresAuth: true,
        roles: ['produceStoreOutOrderGet'],
        hideInMenu: true,
      },
    },
    {
      path: 'outbound-order-detail',
      name: 'outboundOrderDetail',
      component: () => import('@/views/production/outboundOrder/detail.vue'),
      meta: {
        title: '生产出库单详情',
        requiresAuth: true,
        roles: ['produceStoreOutOrderGet'],
        hideInMenu: true,
      },
    },
    {
      path: 'production-outsource',
      name: 'ProductionOutsource',
      component: () =>
        import('@/views/production/productionOutsource/index.vue'),
      meta: {
        title: '工序外发管理',
        requiresAuth: true,
        roles: ['outwardOrderGet'],
      },
    },
    {
      path: 'production-outsource-details',
      name: 'ProductionOutsourceDetails',
      component: () =>
        import('@/views/production/productionOutsource/details/index.vue'),
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
      }),
      meta: {
        title: '外发详情',
        requiresAuth: true,
        hideInMenu: true,
        roles: ['outwardOrderGet'],
      },
    },
    {
      path: 'work-ticket',
      name: 'WorkTicket',
      component: () => import('@/views/production/workTicket/index.vue'),
      meta: {
        title: '裁床编菲',
        requiresAuth: true,
        roles: ['produceOrderGet', 'cutBedTicketGet'],
        notOnline: false,
      },
    },
    {
      path: 'work-ticket-info',
      name: 'WorkTicketInfo',
      component: () =>
        import('@/views/production/workTicket/workTicketInfo.vue'),
      meta: {
        title: '裁床编菲明细',
        requiresAuth: true,
        roles: ['produceOrderGet', 'cutBedTicketGet'],
        notOnline: false,
        hideInMenu: true,
        ignoreCache: true,
      },
    },
  ],
};

export default Production;
